import { useState } from "react";
import { usePortalStore } from "@/store/portal-store";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { Step1Landing } from "@/steps/Step1Landing";
import { Step2Identity } from "@/steps/Step2Identity";
import { Step3DOTLookup } from "@/steps/Step3DOTLookup";
import { Step4VinAcv } from "@/steps/Step4VinAcv";
import { Step5UWQuestions } from "@/steps/Step5UWQuestions";
import { Step6Quote } from "@/steps/Step6Quote";
import { Step7Photos } from "@/steps/Step7Photos";
import { Step8MVR } from "@/steps/Step8MVR";
import { Step9Payment } from "@/steps/Step9Payment";
import { Step10Confirmation } from "@/steps/Step10Confirmation";
import { AddTrailerModal } from "@/steps/AddTrailerModal";
import { AddDriverModal } from "@/steps/AddDriverModal";
import "./index.css";

export function App() {
  const {
    state,
    setStep,
    nextStep,
    updateState,
    updateConsent,
    updateUWAnswers,
    updateQuote,
    setPhoto,
  } = usePortalStore();

  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const renderStep = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <Step1Landing
            state={state}
            updateConsent={updateConsent}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <Step2Identity
            onNext={nextStep}
            onBlock={() => {
              /* blocked state handled internally */
            }}
          />
        );
      case 3:
        return (
          <Step3DOTLookup
            state={state}
            updateState={updateState}
            onNext={nextStep}
          />
        );
      case 4:
        return (
          <Step4VinAcv
            state={state}
            updateState={updateState}
            onNext={nextStep}
          />
        );
      case 5:
        return (
          <Step5UWQuestions
            state={state}
            updateUWAnswers={updateUWAnswers}
            onNext={() => {
              // Generate a quote and advance
              const isLeased = state.uwAnswers.driverStatus === "leased";
              updateState({
                quote: {
                  monthlyPremium: 847,
                  baseALPremium: 780,
                  stateFees: 67,
                  ntlPrice: isLeased ? 125 : 0,
                  ocacPrice: 89,
                  ntlIncluded: isLeased,
                  ocacAdded: false,
                  ntlDeclined: false,
                  ocacDeclined: false,
                  trailers: [],
                  teamDrivers: [],
                },
              });
              nextStep();
            }}
          />
        );
      case 6:
        return (
          <Step6Quote
            state={state}
            updateState={updateState}
            onNext={nextStep}
            onAddTrailer={() => setShowTrailerModal(true)}
            onAddDriver={() => setShowDriverModal(true)}
          />
        );
      case 7:
        return (
          <Step7Photos state={state} setPhoto={setPhoto} onNext={nextStep} />
        );
      case 8:
        return (
          <Step8MVR
            state={state}
            updateState={updateState}
            onNext={nextStep}
          />
        );
      case 9:
        return (
          <Step9Payment
            state={state}
            updateState={updateState}
            onNext={nextStep}
          />
        );
      case 10:
        return <Step10Confirmation state={state} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white max-w-md mx-auto relative">
      {/* Progress indicator - hidden on step 1 and 10 */}
      {state.currentStep > 1 && state.currentStep < 10 && (
        <ProgressIndicator currentStep={state.currentStep} />
      )}

      {/* Main content */}
      <div
        className={
          state.currentStep > 1 && state.currentStep < 10 ? "pt-16" : ""
        }
      >
        {renderStep()}
      </div>

      {/* Toast notification */}
      {toast && (
        <div className="fixed top-20 left-4 right-4 max-w-md mx-auto bg-gray-800 text-white px-4 py-3 rounded-xl text-sm text-center z-50 animate-in fade-in slide-in-from-top-2">
          {toast}
        </div>
      )}

      {/* Sub-flow modals */}
      {showTrailerModal && (
        <AddTrailerModal
          onAdd={(trailer) => {
            if (state.quote) {
              updateState({
                quote: {
                  ...state.quote,
                  trailers: [...state.quote.trailers, trailer],
                },
              });
            }
            setShowTrailerModal(false);
            showToast("Trailer added to your quote");
          }}
          onClose={() => setShowTrailerModal(false)}
        />
      )}
      {showDriverModal && (
        <AddDriverModal
          onAdd={(driver) => {
            if (state.quote) {
              updateState({
                quote: {
                  ...state.quote,
                  teamDrivers: [...state.quote.teamDrivers, driver],
                },
              });
            }
            setShowDriverModal(false);
            showToast("Team driver added");
          }}
          onClose={() => setShowDriverModal(false)}
        />
      )}
    </div>
  );
}

export default App;
